// Informations IA sur les ventes — insights générés automatiquement
import { TrendingUp, AlertTriangle, BarChart2, RefreshCw } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import { useSalesInsights } from "@/hooks/admin/useAI";
import type { InsightCategory, SalesInsight } from "@/lib/admin-api/ai";
import React from "react";

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatFcfa(n: number): string {
  return `${n.toLocaleString("fr-SN")} FCFA`;
}

function formatDate(d: string): string {
  return new Date(d).toLocaleDateString("fr-SN");
}

// ---------------------------------------------------------------------------
// Category helpers
// ---------------------------------------------------------------------------

const CATEGORY_LABELS: Record<InsightCategory, string> = {
  opportunity: "Opportunité",
  risk: "Risque",
  trend: "Tendance",
};

const CATEGORY_COLORS: Record<InsightCategory, string> = {
  opportunity: "bg-blue-100 text-blue-700",
  risk: "bg-red-100 text-red-700",
  trend: "bg-purple-100 text-purple-700",
};

const CATEGORY_ICONS: Record<InsightCategory, React.ElementType> = {
  opportunity: TrendingUp,
  risk: AlertTriangle,
  trend: BarChart2,
};

// ---------------------------------------------------------------------------
// Insight card
// ---------------------------------------------------------------------------

function InsightCard({ insight }: { readonly insight: SalesInsight }) {
  const Icon = CATEGORY_ICONS[insight.category];
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
      <div className="mb-3 flex items-start justify-between">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${CATEGORY_COLORS[insight.category]}`}
        >
          <Icon className="h-3.5 w-3.5" />
          {CATEGORY_LABELS[insight.category]}
        </span>
        <span className="text-xs text-stone-400">
          {insight.confidence_percent}% confiance
        </span>
      </div>

      <h3 className="text-sm font-semibold text-stone-900">{insight.title}</h3>
      <p className="mt-1.5 text-sm leading-relaxed text-stone-500">{insight.description}</p>

      <div className="mt-4 flex items-center justify-between border-t border-stone-100 pt-3">
        {insight.impact_fcfa != null ? (
          <span className="text-xs font-medium text-stone-700">
            Impact estimé : <span className="text-stone-900">{formatFcfa(insight.impact_fcfa)}</span>
          </span>
        ) : (
          <span className="text-xs text-stone-400">Impact non chiffré</span>
        )}
        <span className="text-xs text-stone-400">{formatDate(insight.created_at)}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function SalesInsights() {
  const { data, isLoading, isError, refetch, isFetching } = useSalesInsights();

  const insights = data?.insights ?? [];

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-stone-900">Informations IA sur les ventes</h1>
            <p className="mt-1 text-sm text-stone-500">
              Insights générés automatiquement par l'IA à partir de vos données de vente
              {data?.generated_at && (
                <span className="ml-2 text-stone-400">
                  — Générés le {new Date(data.generated_at).toLocaleDateString("fr-SN")}
                </span>
              )}
            </p>
          </div>
          <button
            type="button"
            disabled={isFetching}
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 rounded-md bg-stone-900 px-4 py-2 text-sm font-medium text-white hover:bg-stone-800 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            Régénérer les insights
          </button>
        </div>

        {/* Content */}
        {isError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            Impossible de charger les insights. Veuillez réessayer.
          </div>
        ) : isLoading ? (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-40 animate-pulse rounded-xl border border-stone-200 bg-stone-100" />
            ))}
          </div>
        ) : insights.length === 0 ? (
          <EmptyState
            title="Aucun insight disponible"
            description="L'IA n'a pas encore généré d'informations pour cette période."
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {insights.map((insight) => (
              <InsightCard key={insight.id} insight={insight} />
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
